import {observable, makeObservable, computed} from 'mobx';
import {compact, countBy, Dictionary, every, filter, first, flatten, forEach, groupBy, isEmpty, keys, map, max, min,
  nth, size, some, sortBy, take, transform, uniq, uniqBy} from 'lodash';

import {ReactNode} from 'react';

import Node from './Node';
import LinksGroup from './LinksGroup';
import Port from './Port';
import RackStore from './RackStore';
import SelectionStore from '../../cablingMapEditor/store/SelectionStore';
import {NODE_ROLES, PORT_ROLES, REDUNDANCY} from '../const';
import {SpeedPortsCountType} from '../types';

class RackEditorStore {
  @observable rackStore: RackStore;
  @observable selection: SelectionStore;

  @computed
  get selectedNodes(): Node[] {
    return compact(map(this.selection.selectedNodesIds, (nodeId) => this.rackStore.nodes[nodeId]));
  }

  @computed
  get namesInvolved(): string[] {
    return uniq(map(this.selectedNodes, 'name'));
  }

  @computed
  get nodesLinksGroups(): LinksGroup[] {
    return filter(this.rackStore.linksGroups, (linksGroup) => linksGroup.containsNames(this.namesInvolved, true));
  }

  @computed
  get selectedLinksGroups(): LinksGroup[] {
    return filter(this.rackStore.linksGroups, (linksGroup) => {
      return !linksGroup.isPeer && linksGroup.connectsNames(this.namesInvolved) && !linksGroup.isDraft;
    });
  }

  // Links can be managed between pairs thus more than two nodes might identify a link
  @computed
  get selectedNames(): string[] {
    return keys(groupBy(this.selectedNodes, 'name'));
  }

  @computed
  get twoNamesSelected(): boolean {
    return size(this.selectedNames) === 2;
  }

  // True if selected nodes are peers
  @computed
  get arePeers(): boolean {
    return this.selection.twoSelected &&
      !!first(this.selectedNodes)?.isPairedWith(nth(this.selectedNodes, 1)!.id);
  }

  @computed
  get twoPairs(): boolean {
    return (this.twoNamesSelected && every(this.selectedNodes, {isPaired: true}));
  }

  // Link direction matters. It always come like generic->switch|leaf, access->leaf
  @computed
  get linkNodes(): Node[] | [null, null] {
    if (this.arePeers) {
      return take(this.selectedNodes, 2);
    } else if (!this.twoNamesSelected) {
      return [null, null];
    } else {
      return sortBy(
        compact(map(this.selectedNames, (name) => first<Node>(this.rackStore.nodesByName[name]))),
        'roleIndex'
      );
    }
  }

  @computed
  get sourceNode(): Node | null {
    return first(this.linkNodes) ?? null;
  }

  @computed
  get destinationNode(): Node | null {
    return nth(this.linkNodes, 1) ?? null;
  }

  // It is forbidden to create links between non-peer nodes of the same role
  @computed
  get sameRole(): boolean {
    return !this.arePeers && !!this.sourceNode && this.sourceNode.role === this.destinationNode?.role;
  }

  @computed
  get rolesCount(): Dictionary<number> {
    // Switch pairs are counted as one
    return countBy(uniqBy(this.selectedNodes, 'name'), 'role');
  }

  @computed
  get leafsCount(): number {
    return this.rolesCount[NODE_ROLES.LEAF];
  }

  @computed
  get accessSwitchesCount(): number {
    return this.rolesCount[NODE_ROLES.ACCESS_SWITCH];
  }

  @computed
  get genericsCount(): number {
    return this.rolesCount[NODE_ROLES.GENERIC];
  }

  @computed
  get allEsis(): boolean {
    return every(this.selectedNodes, {redundancyProtocol: REDUNDANCY.ESI});
  }

  @computed
  get allGenerics(): boolean {
    return every(this.selectedNodes, {role: NODE_ROLES.GENERIC});
  }

  @computed
  get noLinksExist(): boolean {
    return isEmpty(this.nodesLinksGroups);
  }

  @computed
  get enoughPortsForNewLink(): boolean {
    return some(this.portsSpeeds, (freeCount) => freeCount > 0);
  }

  // If multiple (>1) leafs and generics selected with no access switches - multiple links management is possible
  @computed
  get multipleLinks(): boolean {
    return !this.accessSwitchesCount &&
      ((this.leafsCount > 0 && this.genericsCount > 1) || (this.leafsCount > 1 && this.genericsCount > 0));
  }

  // Reason why new link creation is not possible
  @computed
  get newLinkRestriction(): string | null {
    if (this.selection.twoSelected) {
      const roleRestriction = this.rackStore.getLinkingRoleRestrictionFor(this.sourceNode, this.destinationNode);
      if (roleRestriction) {
        return roleRestriction;
      }
    }
    if (!this.enoughPortsForNewLink && !this.rackStore.hasDraftLinks) {
      return 'You have used all available ports, and can not create more link groups';
    }
    return null;
  }

  // Calculate amounts of free ports of desired role on each of the nodes involved
  @computed
  get ports(): {free: SpeedPortsCountType[][], node: Dictionary<SpeedPortsCountType>} {
    if (!this.sourceNode && !this.multipleLinks) {
      return {free: [], node: {}};
    }

    const nodePorts = {};
    const free: SpeedPortsCountType[][] = transform(
      (this.multipleLinks ? this.selectedNodes : [this.sourceNode, this.destinationNode]) as Node[],
      (acc: SpeedPortsCountType[][], node: Node, index) => {
        const oppositePortRole = this.multipleLinks ?
          // If multiple nodes are selected only link GENERIC to LEAF,
          (node.isLeaf ? PORT_ROLES.GENERIC : PORT_ROLES.LEAF) :
          // otherwise PEER is also possible
          (this.arePeers ? PORT_ROLES.PEER : (index ? this.sourceNode?.role : this.destinationNode?.role));
        if (!oppositePortRole) return;

        acc.push(
          map(
            node.nodesInvolved,
            (nodeInvolved) => {
              const ports = countBy(
                filter(
                  nodeInvolved.availablePorts,
                  (port: Port) => port.matchesRole(oppositePortRole, true)
                ),
                'speedString'
              );
              nodePorts[nodeInvolved.id] = ports;
              return ports;
            }
          )
        );
      },
      []
    );
    return {free, node: nodePorts};
  }

  @computed
  get canManageLinks(): {result: boolean, why: ReactNode} {
    // Links can not be managed if:
    let why: string | null = null;
    let result = true;

    // * Not just two names selected
    // * And selected nodes are not peers
    if (!this.twoNamesSelected && !this.arePeers && !this.multipleLinks) {
      // Disable linking silently
      result = false;
    } else if (this.allGenerics) {
      // * No links between generic systems
      why = 'Links cannot be created between generic systems';
      result = false;
    } else if (this.arePeers) {
      // * Selected peers are a ESI leaf-pair
      if (this.allEsis && first(this.selectedNodes)?.isLeaf) {
        why = 'ESI leaf pair can not have peer links';
        result = false;
      }
    } else {
      if (this.twoPairs) {
        // * Switch pairs of the different redundancy type (ESI -> MLAG)
        if (!this.allEsis) {
          why = 'Only switch pairs of the same redundancy type linking is allowed';
          result = false;
        }
      } else if (this.multipleLinks && isEmpty(this.portsSpeeds) && !this.rackStore.hasDraftLinks) {
        why = 'Not enough free ports of the same speed to create multiple links';
        result = false;
      }

      // The rest of the common checks
      if (result && this.noLinksExist) {
        why = this.newLinkRestriction;
        result = !why;
      }
    }
    return {result, why};
  }

  // Calculate ports intersection per speed
  @computed
  get portsSpeeds(): Dictionary<number> {
    const {rackStore: {nodes}, sourceNode, destinationNode, ports: {free: freePorts, node: nodePorts}, sameRole,
      multipleLinks, genericsCount, leafsCount} = this;

    if ((!sourceNode || !freePorts || sameRole) && !multipleLinks) return {};

    const isOneToPair = !sourceNode?.isPaired && destinationNode?.isPaired;
    const isPairToOne = sourceNode?.isPaired && !destinationNode?.isPaired;

    // Now find ports intersection based on ports speed and possible configuration of links group
    return transform(
      // Iterating through ports speeds first source node has
      keys(freePorts[0][0]),
      (acc, speedString) => {
        const speedPorts = map(
          flatten(this.ports.free),
          (portsCounts) => portsCounts?.[speedString] ?? 0
        );

        let intersection = 0;
        if (isOneToPair) {
          // For that type of link it is enough that source and ONE of destination nodes
          // to have intersecting ports
          intersection = min([speedPorts[0], max([speedPorts[1], speedPorts[2]])]) || 0;
        } else {
          // In other cases all nodes must have free ports available
          const ports = compact(speedPorts);
          const compactSize = size(ports);
          if (isPairToOne && compactSize === 3 && ports[0] > 1) {
            // When a pair is connected to a single node, both source nodes must have free ports and
            // the destination must have at least two
            intersection = min(ports) || 0;
          } else if (multipleLinks) {
            // Multiple links management (multiple generics to multiple leafs)
            const a = map(nodePorts, (ports, nodeId) => {
              const {isLeaf} = nodes[nodeId];
              return Math.floor((ports?.[speedString] ?? 0) / (isLeaf ? genericsCount : leafsCount));
            });
            intersection = min(a) || 0;
          } else if (!isPairToOne && compactSize === size(speedPorts)) {
            // In all other cases all nodes must have intersected amount of ports
            intersection = min(ports) || 0;
          }
        }

        if (intersection > 0) acc[speedString] = intersection;
      },
      {}
    );
  }

  // Identifies whether selection cloning is possible
  get canClone(): {result: boolean, why: ReactNode} {
    const {selection: {nothingSelected}, rackStore: {linksGroups, nodesByName, nodes}, namesInvolved} = this;

    if (nothingSelected) return {result: false, why: null};

    const portsRequired = transform(linksGroups, (acc, linksGroup) => {
      const {fromName, toName, speed, isPeer, fromRole, toRole} = linksGroup;
      const containsFrom = namesInvolved.includes(fromName);
      const containsTo = namesInvolved.includes(toName);

      // Identify links that go from selected subset to outside
      const [outsideName, insideRole] = containsFrom ?
        (containsTo ? [null, null] : [toName, fromRole]) :
        (containsTo ? [fromName, toRole] : [null, null]);

      // Peer links do not take part in cloning
      if (outsideName && !isPeer) {
        const nodesLinksPorts = linksGroup.getSizeOnNodes(nodesByName);
        forEach(nodesByName[outsideName], (node) => {
          const id = node?.id;
          (acc[id] ??= []).push(...Port.generate(nodesLinksPorts[id], speed, insideRole!));
        });
      }
    }, {});

    let why: ReactNode = null;
    // Cloning is possible if all involved nodes have enough of free ports of needed speeds
    return {
      result: every(portsRequired, (portsNeeded, nodeId) => {
        const node = nodes?.[nodeId];
        const result = node?.canConnectPorts(portsNeeded);
        if (!result) {
          why = <><b>{node?.label}</b>{' does not have enough free ports to clone selected nodes'}</>;
        }
        return result;
      }),
      why
    };
  }

  @computed
  get managedLinkId(): string | null {
    return (this.canManageLinks.result && !this.newLinkRestriction) ?
      null :
      (first(this.nodesLinksGroups)?.id ?? null);
  }

  constructor() {
    makeObservable(this);
    this.rackStore = new RackStore(this);
    this.selection = new SelectionStore(this);
  }

  dispose() {
  }
}

export default RackEditorStore;
